import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, Pencil, Plus, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { api, ApiError } from '../lib/api';
import { Button } from './ui';
import type { ConversationTag, Tag } from '../types';

/**
 * Tag-Verwaltung einer Konversation. Zeigt alle Tags des Postfachs als
 * Checkbox-Liste (Häkchen = der Konversation zugewiesen), erlaubt Zuweisen/Entfernen,
 * Anlegen (Name + Farbe), Umbenennen und Löschen.
 *
 * Eingebettet als Inhalt eines Popovers (ConversationActionsMenu) — bringt daher
 * keine eigene Backdrop/Esc-Mechanik mit, sondern lebt im Fokus-/Esc-Handling des Popovers.
 */

const PRESET_COLORS = [
  '#2563eb',
  '#06b6d4',
  '#10b981',
  '#f59e0b',
  '#ef4444',
  '#8b5cf6',
  '#ec4899',
  '#64748b',
];

interface Props {
  conversation: { id: string; channelAccount: { id: string } };
  tags: ConversationTag[];
  onClose: () => void;
}

export function TagPopover({ conversation, tags, onClose }: Props) {
  const queryClient = useQueryClient();
  const channelAccountId = conversation.channelAccount.id;

  const assignedIds = new Set(tags.map((t) => t.tag.id));

  const { data, isLoading } = useQuery({
    queryKey: ['channel-account-tags', channelAccountId],
    queryFn: () => api.get<{ items: Tag[] }>(`/api/channel-accounts/${channelAccountId}/tags`),
  });
  const allTags = data?.items ?? [];

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['conversations'] });
    queryClient.invalidateQueries({ queryKey: ['conversation', conversation.id] });
    queryClient.invalidateQueries({ queryKey: ['channel-account-tags', channelAccountId] });
  }

  const assign = useMutation({
    mutationFn: (tagId: string) =>
      api.post(`/api/conversations/${conversation.id}/tags`, { tagId }),
    onSuccess: invalidate,
    onError: (err) => {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined;
      if (body?.error === 'tag_scope_mismatch') {
        toast.error('Dieser Tag gehört zu einem anderen Postfach.');
      } else {
        toast.error('Tag konnte nicht zugewiesen werden.');
      }
    },
  });

  const unassign = useMutation({
    mutationFn: (tagId: string) =>
      api.del(`/api/conversations/${conversation.id}/tags/${tagId}`),
    onSuccess: invalidate,
    onError: () => toast.error('Tag konnte nicht entfernt werden.'),
  });

  const createTag = useMutation({
    mutationFn: (input: { name: string; color: string }) =>
      api.post<Tag>(`/api/channel-accounts/${channelAccountId}/tags`, input),
    onSuccess: async (tag) => {
      queryClient.invalidateQueries({ queryKey: ['channel-account-tags', channelAccountId] });
      // Eingabe erst nach Erfolg zurücksetzen — bei 409 bleibt der getippte Name erhalten.
      setNewName('');
      setCreating(false);
      // Frisch angelegten Tag direkt zuweisen.
      assign.mutate(tag.id);
    },
    onError: (err) => {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined;
      if (body?.error === 'tag_exists') toast.error('Ein Tag mit diesem Namen existiert bereits.');
      else toast.error('Tag konnte nicht angelegt werden.');
    },
  });

  const renameTag = useMutation({
    mutationFn: (input: { id: string; name: string }) =>
      api.patch<Tag>(`/api/tags/${input.id}`, { name: input.name }),
    onSuccess: () => {
      invalidate();
      setEditingId(null);
    },
    onError: (err) => {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined;
      if (body?.error === 'tag_exists') toast.error('Ein Tag mit diesem Namen existiert bereits.');
      else toast.error('Tag konnte nicht umbenannt werden.');
    },
  });

  const deleteTag = useMutation({
    mutationFn: (id: string) => api.del(`/api/tags/${id}`),
    onSuccess: () => {
      invalidate();
      setConfirmDeleteId(null);
    },
    onError: () => toast.error('Tag konnte nicht gelöscht werden.'),
  });

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState(PRESET_COLORS[0]);
  const newInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (creating) newInputRef.current?.focus();
  }, [creating]);

  function toggle(tag: Tag) {
    if (assignedIds.has(tag.id)) unassign.mutate(tag.id);
    else assign.mutate(tag.id);
  }

  function submitNew() {
    const name = newName.trim();
    if (!name) return;
    createTag.mutate({ name, color: newColor });
  }

  return (
    <div className="w-72 p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="kalkuel-eyebrow">Tags</span>
        <button
          type="button"
          onClick={onClose}
          title="Schließen"
          className="rounded-pill p-0.5 text-kalkuel-muted hover:bg-kalkuel-surface hover:text-kalkuel-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-kalkuel-accent/40"
        >
          <X size={14} />
        </button>
      </div>

      <ul className="max-h-56 space-y-0.5 overflow-y-auto">
        {isLoading && <li className="px-1 py-2 text-xs text-kalkuel-muted">Lädt…</li>}
        {!isLoading && allTags.length === 0 && (
          <li className="px-1 py-2 text-xs text-kalkuel-muted">Noch keine Tags für dieses Postfach.</li>
        )}
        {allTags.map((tag) => {
          const checked = assignedIds.has(tag.id);
          if (confirmDeleteId === tag.id) {
            return (
              <li
                key={tag.id}
                className="flex items-center justify-between gap-2 rounded-md bg-red-50 px-2 py-1.5 text-xs"
              >
                <span className="text-red-700">„{tag.name}" löschen?</span>
                <span className="flex shrink-0 items-center gap-1">
                  <button
                    type="button"
                    onClick={() => deleteTag.mutate(tag.id)}
                    className="rounded-pill bg-red-600 px-2 py-0.5 font-medium text-white hover:bg-red-700"
                  >
                    Löschen
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmDeleteId(null)}
                    className="rounded-pill px-2 py-0.5 text-kalkuel-muted hover:bg-white"
                  >
                    Abbrechen
                  </button>
                </span>
              </li>
            );
          }
          if (editingId === tag.id) {
            return (
              <li key={tag.id} className="flex items-center gap-1.5 px-1 py-1">
                <input
                  autoFocus
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const name = editName.trim();
                      if (name) renameTag.mutate({ id: tag.id, name });
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      e.stopPropagation();
                      setEditingId(null);
                    }
                  }}
                  className="h-7 flex-1 rounded-md border border-kalkuel-border px-2 text-xs focus:border-kalkuel-accent focus:outline-none focus:ring-2 focus:ring-kalkuel-accent/30"
                />
                <button
                  type="button"
                  onClick={() => {
                    const name = editName.trim();
                    if (name) renameTag.mutate({ id: tag.id, name });
                  }}
                  title="Speichern"
                  className="rounded-pill p-1 text-kalkuel-accent2 hover:bg-kalkuel-accent-soft"
                >
                  <Check size={13} />
                </button>
                <button
                  type="button"
                  onClick={() => setEditingId(null)}
                  title="Abbrechen"
                  className="rounded-pill p-1 text-kalkuel-muted hover:bg-kalkuel-surface"
                >
                  <X size={13} />
                </button>
              </li>
            );
          }
          return (
            <li
              key={tag.id}
              className="group flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-kalkuel-surface"
            >
              <button
                type="button"
                role="checkbox"
                aria-checked={checked}
                aria-label={tag.name}
                onClick={() => toggle(tag)}
                className="flex min-w-0 flex-1 items-center gap-2 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-kalkuel-accent/40 rounded"
              >
                <span
                  className={[
                    'flex size-4 shrink-0 items-center justify-center rounded border',
                    checked ? 'border-transparent text-white' : 'border-kalkuel-border',
                  ].join(' ')}
                  style={checked ? { backgroundColor: tag.color } : undefined}
                >
                  {checked && <Check size={11} />}
                </span>
                <span
                  className="size-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: tag.color }}
                  aria-hidden
                />
                <span className="truncate text-xs text-kalkuel-primary">{tag.name}</span>
              </button>
              <span className="flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                <button
                  type="button"
                  onClick={() => {
                    setEditingId(tag.id);
                    setEditName(tag.name);
                  }}
                  title="Umbenennen"
                  className="rounded-pill p-1 text-kalkuel-muted hover:bg-white hover:text-kalkuel-primary"
                >
                  <Pencil size={12} />
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmDeleteId(tag.id)}
                  title="Löschen"
                  className="rounded-pill p-1 text-red-600 hover:bg-red-50"
                >
                  <Trash2 size={12} />
                </button>
              </span>
            </li>
          );
        })}
      </ul>

      <div className="mt-2 border-t border-kalkuel-border pt-2">
        {creating ? (
          <div className="space-y-2">
            <input
              ref={newInputRef}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  submitNew();
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  e.stopPropagation();
                  setCreating(false);
                }
              }}
              placeholder="Tag-Name…"
              className="h-7 w-full rounded-md border border-kalkuel-border px-2 text-xs focus:border-kalkuel-accent focus:outline-none focus:ring-2 focus:ring-kalkuel-accent/30"
            />
            <div className="flex items-center gap-1.5">
              {PRESET_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setNewColor(c)}
                  title={c}
                  aria-label={`Farbe ${c}`}
                  aria-pressed={newColor === c}
                  className={[
                    'size-5 rounded-full transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-kalkuel-accent/50',
                    newColor === c ? 'ring-2 ring-kalkuel-primary ring-offset-1' : '',
                  ].join(' ')}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
            <div className="flex justify-end gap-1.5">
              <Button variant="ghost" size="sm" onClick={() => setCreating(false)}>
                Abbrechen
              </Button>
              <Button size="sm" onClick={submitNew} disabled={!newName.trim() || createTag.isPending}>
                Anlegen
              </Button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium text-kalkuel-primary hover:bg-kalkuel-surface focus:outline-none focus-visible:ring-2 focus-visible:ring-kalkuel-accent/40"
          >
            <Plus size={13} />
            Neuer Tag
          </button>
        )}
      </div>
    </div>
  );
}
